/**
 * Enhanced modern SVG Pie / Donut Chart
 * ─────────────────────────────────────
 * • Segment gaps for a crisp separated look
 * • Per-slice radial gradient fills for depth
 * • Smooth entrance animation on mount
 * • Midpoint-anchored tooltips that follow the hovered slice
 * • Pill-badge legend with count + pct
 * • Glow filter on hover
 * Pure React — zero external dependencies.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';

export interface PieSlice {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieSlice[];
  /** SVG half-width — actual SVG is size×2 (default 96) */
  size?: number;
  /** Stroke width of ring segments — 0 = filled pie (default 30) */
  ringWidth?: number;
  /** Show center total label */
  showTotal?: boolean;
  /** Centre label override (defaults to total count) */
  centerLabel?: string;
  /** Subtitle under the center label */
  centerSub?: string;
}

/* ─── Vivid 10-colour palette ───────────────────────────────── */
export const PIE_COLORS = [
  '#06B6D4', // cyan
  '#6366F1', // indigo
  '#F59E0B', // amber
  '#10B981', // emerald
  '#F43F5E', // rose
  '#8B5CF6', // violet
  '#0EA5E9', // sky
  '#F97316', // orange
  '#14B8A6', // teal
  '#EC4899', // pink
];


/* ─── Unique chart ID for gradient defs ─────────────────── */
let _chartCounter = 0;

/* ─── Arc / path helpers ─────────────────────────────────── */
function toRad(deg: number) { return ((deg - 90) * Math.PI) / 180; }

function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const clamp = Math.min(endDeg - startDeg, 359.97);
  const end = startDeg + clamp;
  const x1 = cx + r * Math.cos(toRad(startDeg));
  const y1 = cy + r * Math.sin(toRad(startDeg));
  const x2 = cx + r * Math.cos(toRad(end));
  const y2 = cy + r * Math.sin(toRad(end));
  return `M ${x1} ${y1} A ${r} ${r} 0 ${clamp > 180 ? 1 : 0} 1 ${x2} ${y2}`;
}

function wedgePath(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const clamp = Math.min(endDeg - startDeg, 359.97);
  const end = startDeg + clamp;
  const x1 = cx + r * Math.cos(toRad(startDeg));
  const y1 = cy + r * Math.sin(toRad(startDeg));
  const x2 = cx + r * Math.cos(toRad(end));
  const y2 = cy + r * Math.sin(toRad(end));
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${clamp > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
}

/** Mid-point of an arc in SVG coordinates */
function midPoint(cx: number, cy: number, r: number, startDeg: number, sweep: number) {
  const mid = startDeg + sweep / 2;
  return { x: cx + r * Math.cos(toRad(mid)), y: cy + r * Math.sin(toRad(mid)) };
}

/** Lighten a hex colour by mixing with white */
function lighten(hex: string, amount = 0.55): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.round(((n >> 16) & 0xff) + (255 - ((n >> 16) & 0xff)) * amount);
  const g = Math.round(((n >> 8) & 0xff) + (255 - ((n >> 8) & 0xff)) * amount);
  const b = Math.round((n & 0xff) + (255 - (n & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
}

/* ═══════════════════════════════════════════════════════════ */
export const PieChart: React.FC<PieChartProps> = ({
  data,
  size = 96,
  ringWidth = 30,
  showTotal = true,
  centerLabel,
  centerSub,
}) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const chartId = useRef(`pc${++_chartCounter}`).current;

  /* Entrance animation — trigger one frame after mount */
  useEffect(() => { const id = setTimeout(() => setMounted(true), 30); return () => clearTimeout(id); }, []);

  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);

  if (total === 0 || data.length === 0) {
    return (
      <div style={st.wrapper}>
        <div style={st.svgWrap}>
          <svg width={size * 2} height={size * 2} viewBox={`0 0 ${size * 2} ${size * 2}`}>
            <circle cx={size} cy={size} r={size - 10} fill="none" stroke="#EEF1F5" strokeWidth={ringWidth || 10} />
          </svg>
        </div>
        <p style={st.emptyText}>No data</p>
      </div>
    );
  }

  const filled = ringWidth === 0;
  const cx = size, cy = size;
  const r = filled ? size - 6 : size - 8;
  const GAP = data.length > 1 ? 2.8 : 0; // degrees of gap per side

  const arcs = (() => {
    let angle = 0;
    return data.map((d, i) => {
      const sweep = (d.value / total) * 360;
      const start = angle;
      angle += sweep;
      const adjStart = start + GAP;
      const adjSweep = Math.max(sweep - GAP * 2, 0.5);
      return { ...d, start, sweep, adjStart, adjSweep, index: i };
    });
  })();

  const svgSize = size * 2;

  return (
    <div style={st.wrapper}>
      {/* ── SVG ── */}
      <div style={st.svgWrap}>
        <svg
          width={svgSize}
          height={svgSize}
          viewBox={`0 0 ${svgSize} ${svgSize}`}
          style={{ overflow: 'visible', filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.06))', maxWidth: '100%' }}
        >
          <defs>
            {/* Per-slice radial gradient */}
            {arcs.map((arc) => (
              <radialGradient
                key={arc.index}
                id={`rg-${chartId}-${arc.index}`}
                cx="50%" cy="50%" r="70%"
              >
                <stop offset="0%" stopColor={lighten(arc.color, 0.35)} />
                <stop offset="100%" stopColor={arc.color} />
              </radialGradient>
            ))}
            {/* Glow filter */}
            <filter id={`glow-${chartId}`} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            {/* Soft shadow filter */}
            <filter id={`shadow-${chartId}`}>
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
            </filter>
          </defs>

          {/* Background track */}
          {!filled && (
            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EEF1F5" strokeWidth={ringWidth + 2} />
          )}
          {filled && (
            <circle cx={cx} cy={cy} r={r} fill="#F8FAFC" />
          )}

          {/* Segments */}
          {arcs.map((arc) => {
            const isHov = hovered === arc.index;
            const opacity = hovered !== null && !isHov ? 0.38 : 1;
            const scaleVal = isHov ? 1.07 : 1;
            const fill = `url(#rg-${chartId}-${arc.index})`;
            const transitionStyle = {
              transformOrigin: `${cx}px ${cy}px`,
              transform: mounted ? `scale(${scaleVal})` : 'scale(0.82)',
              opacity: mounted ? opacity : 0,
              transition: 'transform 0.25s cubic-bezier(.34,1.56,.64,1), opacity 0.3s ease',
              cursor: 'pointer',
              filter: isHov ? `url(#glow-${chartId})` : 'none',
            } as React.CSSProperties;

            if (filled) {
              return (
                <path
                  key={arc.index}
                  d={wedgePath(cx, cy, r, arc.adjStart, arc.adjStart + arc.adjSweep)}
                  fill={fill}
                  stroke="#FFF"
                  strokeWidth={2}
                  style={transitionStyle}
                  onMouseEnter={() => setHovered(arc.index)}
                  onMouseLeave={() => setHovered(null)}
                />
              );
            }

            return (
              <path
                key={arc.index}
                d={arcPath(cx, cy, r, arc.adjStart, arc.adjStart + arc.adjSweep)}
                fill="none"
                stroke={fill}
                strokeWidth={isHov ? ringWidth + 7 : ringWidth}
                strokeLinecap="round"
                style={{
                  ...transitionStyle,
                  transition: 'transform 0.25s cubic-bezier(.34,1.56,.64,1), opacity 0.3s ease, stroke-width 0.2s ease',
                }}
                onMouseEnter={() => setHovered(arc.index)}
                onMouseLeave={() => setHovered(null)}
              />
            );
          })}

          {/* White inner circle for filled pie */}
          {filled && (
            <circle
              cx={cx} cy={cy} r={r * 0.44}
              fill="#FFF"
              style={{ filter: `url(#shadow-${chartId})` }}
            />
          )}

          {/* Centre labels */}
          {showTotal && (
            <>
              <text x={cx} y={cy + 1} textAnchor="middle"
                style={{ fontSize: size * 0.26, fontWeight: 800, fill: hovered !== null ? arcs[hovered].color : '#1E293B', transition: 'fill 0.2s', fontFamily: 'inherit' }}>
                {hovered !== null ? arcs[hovered].value : (centerLabel ?? total)}
              </text>
              <text x={cx} y={cy + size * 0.17} textAnchor="middle"
                style={{ fontSize: size * 0.115, fontWeight: 600, fill: '#94A3B8', fontFamily: 'inherit' }}>
                {hovered !== null ? `${Math.round((arcs[hovered].value / total) * 100)}%` : (centerSub ?? 'total')}
              </text>
            </>
          )}

          {/* Midpoint tooltip lines for large segments */}
          {arcs.map((arc) => {
            if (arc.sweep < 15) return null;
            const mp = midPoint(cx, cy, filled ? r * 0.7 : r, arc.start + GAP, arc.sweep - GAP * 2);
            const pct = Math.round((arc.value / total) * 100);
            if (pct < 8) return null;
            return (
              <text
                key={arc.index}
                x={mp.x} y={mp.y}
                textAnchor="middle" dominantBaseline="middle"
                style={{
                  fontSize: size * 0.115,
                  fontWeight: 700,
                  fill: '#FFF',
                  opacity: mounted ? (hovered === null || hovered === arc.index ? 1 : 0) : 0,
                  transition: 'opacity 0.2s',
                  pointerEvents: 'none',
                  textShadow: '0 1px 3px rgba(0,0,0,0.25)',
                  fontFamily: 'inherit',
                }}
              >
                {pct}%
              </text>
            );
          })}
        </svg>

        {/* Floating tooltip anchored near cursor, shown on hover */}
        {hovered !== null && (() => {
          const arc = arcs[hovered];
          const pct = Math.round((arc.value / total) * 100);
          const mp = midPoint(cx, cy, r * (filled ? 1.22 : 1.32), arc.start + GAP, arc.sweep - GAP * 2);
          const left = (mp.x / svgSize) * 100;
          const top = (mp.y / svgSize) * 100;
          return (
            <div style={{
              ...st.tooltip,
              left: `${left}%`, top: `${top}%`,
              borderLeft: `3px solid ${arc.color}`,
              transform: left > 55 ? 'translate(-104%, -50%)' : left < 45 ? 'translate(4%, -50%)' : 'translate(-50%, -110%)',
            }}>
              <div style={{ ...st.tooltipDot, background: arc.color }} />
              <div style={st.tooltipBody}>
                <div style={st.tooltipLabel}>{arc.label}</div>
                <div style={st.tooltipVal}>
                  <span style={{ color: arc.color, fontWeight: 800 }}>{arc.value}</span>
                  <span style={st.tooltipPct}>{pct}%</span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Legend ── */}
      <div style={st.legend}>
        {arcs.map((arc) => {
          const pct = total > 0 ? Math.round((arc.value / total) * 100) : 0;
          const isHov = hovered === arc.index;
          return (
            <div
              key={arc.index}
              style={{
                ...st.legendItem,
                background: isHov ? lighten(arc.color, 0.88) : 'transparent',
                outline: isHov ? `1px solid ${arc.color}44` : '1px solid transparent',
              }}
              onMouseEnter={() => setHovered(arc.index)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* Colour swatch */}
              <div style={{ ...st.swatch, background: `url(#rg-${chartId}-${arc.index})`, backgroundColor: arc.color, boxShadow: isHov ? `0 0 0 2px #fff, 0 0 0 4px ${arc.color}66` : 'none' }} />
              {/* Label */}
              <span style={{ ...st.legendLabel, color: isHov ? arc.color : '#2D3436', fontWeight: isHov ? 700 : 500 }}>
                {arc.label}
              </span>
              {/* Pills */}
              <div style={st.pills}>
                <span style={{ ...st.pill, background: arc.color + '18', color: arc.color }}>
                  {pct}%
                </span>
                <span style={st.countBadge}>{arc.value}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Styles ─────────────────────────────────────────────── */
const st: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 12,
    justifyContent: 'center',
    padding: '4px 0',
    width: '100%',
    overflow: 'hidden' as const,
  },
  svgWrap: { position: 'relative' as const, flexShrink: 0, maxWidth: '100%' },
  emptyText: { fontSize: 13, color: '#94A3B8', margin: '8px 0 0' },

  tooltip: {
    position: 'absolute' as const,
    background: '#FFF',
    borderRadius: 10,
    padding: '7px 12px 7px 10px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.13)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    zIndex: 20,
    pointerEvents: 'none' as const,
    whiteSpace: 'nowrap' as const,
    minWidth: 120,
  },
  tooltipDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  tooltipBody: { display: 'flex', flexDirection: 'column' as const, gap: 1 },
  tooltipLabel: { fontSize: 11, fontWeight: 600, color: '#475569' },
  tooltipVal: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 },
  tooltipPct: { fontSize: 11, color: '#94A3B8' },

  legend: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 3,
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden' as const,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 8px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.15s, outline 0.15s',
    minWidth: 0,
    overflow: 'hidden' as const,
  },
  swatch: {
    width: 12,
    height: 12,
    borderRadius: 4,
    flexShrink: 0,
    transition: 'box-shadow 0.2s',
  },
  legendLabel: {
    flex: 1,
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    transition: 'color 0.15s',
  },
  pills: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  pill: {
    fontSize: 11,
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: 99,
  },
  countBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: '#94A3B8',
    minWidth: 16,
    textAlign: 'right' as const,
  },
};
